import { SIGNUP_PHASES, assertEditableSchema, visibleFieldsForPhase } from "../config/signup-fields.js";
import { ACCOUNT_ELIGIBILITY_POLICY } from "../config/account-eligibility-policy.js";
import { isRegistrationActivationReady } from "../config/registration-state.js";
import { TERMS_CONTENT } from "../config/terms-content.js";
import { createTermsReviewDialog } from "./terms-review-dialog.js";
import {
  birthDatePolicyError,
  earliestSupportedBirthDateISO,
  emailProviderPolicyError,
  isValidSecondLifeAvatarKey,
  isValidSignupEmail,
  latestEligibleBirthDateISO,
  normalizeEmail,
  passwordPolicyError,
  publicIdentityPolicyError,
} from "../validation/signup-validation.js";

function escapeText(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function attribute(name, value) {
  if (value === false || value === undefined || value === null || value === "") return "";
  if (value === true) return ` ${name}`;
  return ` ${name}="${escapeText(value)}"`;
}

function fieldLayoutAttribute(field) {
  return attribute("data-layout", field.layout || "full");
}

function groupFieldMarkup(field) {
  const requiredLabel = field.required ? "Required" : "Optional";
  const kind = field.type === "radio-group" ? "radio" : "checkbox";
  const options = field.options.map((option, index) => {
    const id = `field-${field.id}-${index}`;
    return `
      <label class="choice-option">
        <input id="${escapeText(id)}" name="${escapeText(field.id)}" type="${kind}" value="${escapeText(option.value)}"${attribute("required", field.required && kind === "radio")} aria-describedby="${escapeText(field.id)}-help ${escapeText(field.id)}-error" />
        <span class="choice-mark" aria-hidden="true"><i></i></span>
        <span class="choice-copy"><strong>${escapeText(option.label)}</strong>${option.description ? `<small>${escapeText(option.description)}</small>` : ""}</span>
      </label>`;
  }).join("");
  return `
    <fieldset class="choice-field choice-field--${kind}" data-field-shell="${escapeText(field.id)}"${fieldLayoutAttribute(field)} aria-describedby="${escapeText(field.id)}-help ${escapeText(field.id)}-error">
      <legend class="field-label"><strong>${escapeText(field.label)}</strong><small>${requiredLabel}</small></legend>
      <div class="choice-grid">${options}</div>
      <small class="field-help" id="${escapeText(field.id)}-help">${escapeText(field.help || "")}</small>
      <em id="${escapeText(field.id)}-error" class="field-error" data-error-for="${escapeText(field.id)}"></em>
    </fieldset>`;
}

function fieldMarkup(field, terms) {
  const requiredLabel = field.required ? "Required" : "Optional";
  if (field.type === "radio-group" || field.type === "checkbox-group") return groupFieldMarkup(field);
  if (field.presentation === "terms-dialog") {
    return `
      <section class="terms-review-field" data-field-shell="${escapeText(field.id)}"${fieldLayoutAttribute(field)} aria-labelledby="${escapeText(field.id)}-label" aria-describedby="${escapeText(field.id)}-help ${escapeText(field.id)}-error">
        <div>
          <span class="field-label"><strong id="${escapeText(field.id)}-label">Terms of Service</strong><small>Final checkpoint</small></span>
          <p id="${escapeText(field.id)}-help">${escapeText(field.label)} The standalone Terms page is for reading only and never records agreement.</p>
          <small>${escapeText(terms.policyId)} // ${escapeText(terms.version)} // ${escapeText(terms.contentRevision)} // ${escapeText(terms.effectiveDate)}</small>
        </div>
        <div class="terms-review-field__actions">
          <button type="button" class="button button--secondary" data-action="review-terms">${escapeText(terms.signupConsent.reviewButtonLabel)}</button>
          <a href="/terms/" target="_blank" rel="noopener">Open reading page</a>
        </div>
        <em id="${escapeText(field.id)}-error" class="field-error" data-error-for="${escapeText(field.id)}"></em>
      </section>`;
  }
  if (field.type === "checkbox") {
    const isEmailVerificationSignal = field.presentation === "email-verification-signal";
    const verificationSignal = isEmailVerificationSignal ? `
      <aside id="email-verification-note" class="email-verification-signal" data-email-verification-signal role="note" aria-labelledby="email-verification-note-title">
        <span class="email-verification-signal__core" aria-hidden="true">
          <svg viewBox="0 0 48 48" focusable="false"><path d="M7.5 12.5h33v23h-33z"/><path d="m9 15 15 12 15-12"/><path d="m9 34 10.5-10M39 34 28.5 24"/></svg>
          <i></i><b></b>
        </span>
        <span class="email-verification-signal__copy">
          <strong id="email-verification-note-title">Email access required</strong>
          <span>${escapeText(ACCOUNT_ELIGIBILITY_POLICY.email.accessReminder)}</span>
          <small>Locked preview // no verification message is sent or stored here. When registration opens, the link expires after 30 minutes; if unused, the unverified registration is removed and you must start again.</small>
        </span>
        <span class="email-verification-signal__rail" aria-hidden="true"></span>
      </aside>` : "";
    const describedBy = isEmailVerificationSignal
      ? "email-verification-note emailAccessConfirmed-error"
      : `${field.id}-error`;
    return `
      ${verificationSignal}
      <div class="check-field" data-field-shell="${escapeText(field.id)}"${fieldLayoutAttribute(field)}>
        <label>
          <input id="field-${escapeText(field.id)}" name="${escapeText(field.id)}" type="checkbox" value="true"${attribute("required", field.required)} aria-describedby="${escapeText(describedBy)}" />
          <span class="check-box" aria-hidden="true"><i></i></span>
          <span class="check-copy">${escapeText(field.label)}${field.link ? ` <a href="${escapeText(field.link.href)}">${escapeText(field.link.label)}</a>` : ""}</span>
        </label>
        <em id="${escapeText(field.id)}-error" class="field-error" data-error-for="${escapeText(field.id)}"></em>
      </div>`;
  }

  if (field.type === "file") {
    return `
      <section class="profile-picture-field" data-field-shell="${escapeText(field.id)}"${fieldLayoutAttribute(field)} aria-labelledby="${escapeText(field.id)}-label" aria-describedby="${escapeText(field.id)}-help ${escapeText(field.id)}-storage ${escapeText(field.id)}-status ${escapeText(field.id)}-error">
        <span class="field-label"><strong id="${escapeText(field.id)}-label">${escapeText(field.label)}</strong><small>${requiredLabel}</small></span>
        <div class="profile-picture-field__body">
          <div class="profile-picture-preview" data-profile-picture-preview data-state="empty" aria-hidden="true">
            <img data-profile-picture-image alt="" hidden />
            <span data-profile-picture-placeholder><i></i><b>NO IMAGE</b></span>
          </div>
          <div class="profile-picture-field__controls">
            <input class="profile-picture-input" id="field-${escapeText(field.id)}" name="${escapeText(field.id)}" type="file" accept="${escapeText(field.accept)}" aria-describedby="${escapeText(field.id)}-help ${escapeText(field.id)}-storage ${escapeText(field.id)}-status ${escapeText(field.id)}-error" />
            <div class="profile-picture-field__actions">
              <label class="button button--secondary profile-picture-chooser" for="field-${escapeText(field.id)}">Choose picture</label>
              <button type="button" class="button button--secondary profile-picture-remove" data-action="remove-profile-picture" hidden>Remove</button>
            </div>
            <p class="profile-picture-storage" id="${escapeText(field.id)}-storage"><strong>Local preview only.</strong> This file is not saved or carried through verification. After you verify your email and sign in, you must choose the file again to upload it. You may also skip it and add a picture later.</p>
            <p class="profile-picture-status" id="${escapeText(field.id)}-status" data-profile-picture-status role="status" aria-live="polite">No picture selected.</p>
          </div>
        </div>
        <small class="field-help" id="${escapeText(field.id)}-help"><span>${escapeText(field.help || "")}</span></small>
        <em id="${escapeText(field.id)}-error" class="field-error" data-error-for="${escapeText(field.id)}"></em>
      </section>`;
  }

  const common = [
    attribute("name", field.id),
    attribute("id", `field-${field.id}`),
    attribute("autocomplete", field.autocomplete || "off"),
    attribute("required", field.required),
    attribute("placeholder", field.placeholder),
    attribute("minlength", field.minLength),
    attribute("maxlength", field.maxLength),
    attribute("pattern", field.pattern),
    attribute("min", field.type === "date" && field.maximumAgeExclusive
      ? earliestSupportedBirthDateISO(field.maximumAgeExclusive)
      : field.min),
    attribute("max", field.type === "date" && field.minimumAge ? latestEligibleBirthDateISO(field.minimumAge) : field.max),
    attribute("data-sensitive", field.sensitive),
    field.spellcheck === false ? ' spellcheck="false"' : "",
    ` aria-describedby="${escapeText(field.id)}-help ${escapeText(field.id)}-error${
      field.id === "email" || field.id === "confirmEmail" ? " email-verification-note" : ""
    }"`,
  ].join("");

  let control;
  if (field.type === "select") {
    control = `<select${common}>${field.options.map((option) => `<option value="${escapeText(option.value)}">${escapeText(option.label)}</option>`).join("")}</select>`;
  } else if (field.type === "textarea") {
    control = `<textarea${common}${attribute("rows", field.rows || 6)}></textarea>`;
  } else {
    control = `<input type="${escapeText(field.type || "text")}"${common} />`;
  }

  const characterCount = field.type === "textarea" && field.maxLength
    ? `<span class="character-count" data-character-count="${escapeText(field.id)}">0 / ${field.maxLength}</span>`
    : "";
  return `
    <label class="field" data-field-shell="${escapeText(field.id)}"${fieldLayoutAttribute(field)} for="field-${escapeText(field.id)}">
      <span class="field-label"><strong>${escapeText(field.label)}</strong><small>${requiredLabel}</small></span>
      <span class="control-shell">${control}<i class="focus-rail" aria-hidden="true"></i>${field.type === "password" ? '<button class="reveal" type="button" data-reveal>Show</button>' : ""}</span>
      <small class="field-help" id="${escapeText(field.id)}-help"><span>${escapeText(field.help || "")}</span>${characterCount}</small>
      <em id="${escapeText(field.id)}-error" class="field-error" data-error-for="${escapeText(field.id)}"></em>
    </label>`;
}

function buildMarkup(schema, concept, registration, terms) {
  const progress = SIGNUP_PHASES.map((phase) => `
    <li data-progress="${phase.id}"><span>${phase.index}</span><strong>${phase.label}</strong><i></i></li>`).join("");
  const phases = SIGNUP_PHASES.map((phase) => `
    <fieldset class="signup-phase" data-phase="${phase.id}">
      <legend>${escapeText(phase.title)}</legend>
      <p class="phase-description">${escapeText(phase.description)}</p>
      <div class="field-stack">${visibleFieldsForPhase(phase.id, schema).map((field) => fieldMarkup(field, terms)).join("")}</div>
    </fieldset>`).join("");

  return `
    <section class="signup-console" id="signup-panel" aria-labelledby="signup-title" data-concept-form="${escapeText(concept.id)}">
      <div class="console-edge console-edge--top" aria-hidden="true"></div>
      <header class="signup-console__header">
        <div>
          <p class="console-kicker"><span></span> EXILED ID ENROLLMENT</p>
          <h1 id="signup-title">Create your identity</h1>
        </div>
        <div class="secure-state"><i aria-hidden="true"></i><span>Registration</span><strong>${escapeText(registration.statusLabel)}</strong></div>
      </header>
      <p class="preview-safety"><b>${escapeText(registration.statusLabel)}</b> ${escapeText(registration.publicMessage)} Nothing entered here is submitted or stored.</p>
      <ol class="signup-progress" aria-label="Signup progress">${progress}</ol>
      <div class="form-status" role="status" aria-live="polite" aria-atomic="true" data-form-status>Phase 1 of ${SIGNUP_PHASES.length}</div>
      <div class="error-summary" role="alert" tabindex="-1" hidden data-error-summary></div>
      <form method="dialog" novalidate autocomplete="on" aria-labelledby="signup-title" data-signup-form data-testid="signup-form" data-native-submission="blocked">
        ${phases}
        <section class="signup-complete" data-phase="complete" hidden tabindex="-1" role="status" aria-live="polite" aria-atomic="true" aria-labelledby="signup-complete-title">
          <div class="completion-mark" aria-hidden="true"><span></span><i></i></div>
          <p class="console-kicker">REGISTRATION REQUEST RECEIVED</p>
          <h2 id="signup-complete-title">Your enrollment is pending.</h2>
          <p>Activation requires verified email, accepted policy versions, and server approval. A selected profile picture stays only in this browser tab and is not included in the account request.</p>
          <aside id="verification-waiting-notice" class="verification-waiting-notice" aria-labelledby="verification-waiting-title">
            <span class="verification-waiting-notice__icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" focusable="false"><path d="M7.5 12.5h33v23h-33z"/><path d="m9 15 15 12 15-12"/><path d="m9 34 10.5-10M39 34 28.5 24"/></svg>
            </span>
            <span class="verification-waiting-notice__copy">
              <strong id="verification-waiting-title">Check your Inbox now</strong>
              <span>Look for the Exiled Systems verification email. If it is not in your Inbox, check your <b>Spam</b> or <b>Junk</b> folder.</span>
              <small>The verification link expires in 30 minutes. Your account stays inactive until you successfully verify your email.</small>
            </span>
          </aside>
          <p>This preview cannot hand off the selected file. After you verify and sign in, reselect the file to upload it, or add a different picture later.</p>
          <button type="button" class="button button--secondary" data-action="reset">Start again</button>
        </section>
        <footer class="signup-actions">
          <button type="button" class="button button--secondary" data-action="back">Back</button>
          <span class="phase-count"><b data-current-count>01</b> / ${String(SIGNUP_PHASES.length).padStart(2, "0")}</span>
          <button type="submit" class="button button--primary" data-action="next"><span>Continue</span><i aria-hidden="true"></i></button>
        </footer>
        <div data-terms-dialog-mount></div>
      </form>
      <div class="console-edge console-edge--bottom" aria-hidden="true"></div>
    </section>`;
}

function controlsFor(form, fieldId) {
  return [...form.querySelectorAll(`[name="${fieldId}"]`)];
}

function namedValue(form, fieldId) {
  const controls = controlsFor(form, fieldId);
  const selected = controls.find((control) => {
    if (control.type === "checkbox" || control.type === "radio") return control.checked;
    return true;
  });
  return selected?.value ?? "";
}

function resetControls(form) {
  form.querySelectorAll("input, select, textarea").forEach((control) => {
    if (control.type === "checkbox" || control.type === "radio") {
      control.checked = control.defaultChecked;
      return;
    }
    if (control.type === "file") {
      control.value = "";
      return;
    }
    if (control instanceof HTMLSelectElement) {
      let hasDefault = false;
      [...control.options].forEach((option) => {
        option.selected = option.defaultSelected;
        hasDefault ||= option.defaultSelected;
      });
      if (!hasDefault) control.selectedIndex = 0;
      return;
    }
    control.value = control.defaultValue;
  });
}

function valueForField(field, form) {
  const controls = controlsFor(form, field.id);
  if (field.type === "file") return controls[0]?.files?.[0] || null;
  if (field.type === "radio-group") return controls.find((control) => control.checked)?.value || "";
  if (field.type === "checkbox-group") return controls.filter((control) => control.checked).map((control) => control.value);
  if (field.type === "checkbox") return Boolean(controls[0]?.checked);
  return controls[0]?.value ?? "";
}

function hasFieldValue(field, form) {
  const value = valueForField(field, form);
  if (field.type === "file") return Boolean(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return value;
  return field.type === "password" ? value.length > 0 : value.trim().length > 0;
}

function isFieldActive(field, form) {
  if (field.visible === false) return false;
  if (!field.showWhen) return true;
  return String(namedValue(form, field.showWhen.field)) === field.showWhen.equals;
}

function clearControl(control) {
  if (control.type === "checkbox" || control.type === "radio") control.checked = false;
  else control.value = "";
  control.removeAttribute("aria-invalid");
}

function fieldError(field, form) {
  if (!isFieldActive(field, form)) return "";
  const controls = controlsFor(form, field.id);
  if (!controls.length) return "";
  const value = valueForField(field, form);
  if (field.required && !hasFieldValue(field, form)) {
    if (field.type === "radio-group") return "Choose one option.";
    if (field.type === "checkbox" || field.type === "checkbox-group") return "This confirmation or selection is required.";
    return `${field.label} is required.`;
  }
  if (!field.required && !hasFieldValue(field, form)) return "";
  if (field.type === "file") {
    if (!field.acceptedMimeTypes.includes(value.type)) return "Choose a PNG, JPEG, or WebP image.";
    if (!Number.isFinite(value.size) || value.size < 1) return "Choose a non-empty image file.";
    if (value.size > field.maximumBytes) return "Choose an image no larger than 5 MiB.";
    return "";
  }

  const raw = Array.isArray(value) || typeof value === "boolean" ? value : String(value);
  const text = typeof raw === "string" && field.type !== "password" ? raw.trim() : raw;
  if (field.id === "displayName" && text.length < 2) return "Use at least 2 characters.";
  if (field.id === "displayName" || field.id === "handle") {
    const identityError = publicIdentityPolicyError(text, { fieldId: field.id });
    if (identityError) return identityError;
  }
  if (field.id === "handle" && !/^[a-z0-9][a-z0-9_-]{2,23}$/.test(text)) return "Use 3–24 lowercase letters, numbers, _ or -.";
  if (field.type === "email" && !isValidSignupEmail(text)) return "Enter a valid email address.";
  if (field.type === "email") {
    const providerError = emailProviderPolicyError(text);
    if (providerError) return providerError;
  }
  if (field.id === "confirmEmail" && normalizeEmail(text) !== normalizeEmail(namedValue(form, "email"))) return "The email addresses must match.";
  if (field.id === "avatarKey" && !isValidSecondLifeAvatarKey(text)) return "Enter a 36-character Second Life avatar key in UUID format.";
  if (field.id === "dateOfBirth") return birthDatePolicyError(text, {
    minimumAge: field.minimumAge,
    maximumAgeExclusive: field.maximumAgeExclusive,
  });
  if (field.id === "password") return passwordPolicyError(raw);
  if (field.id === "confirmPassword" && raw !== String(namedValue(form, "password"))) return "The passwords must match exactly.";
  if (typeof text === "string" && Number.isInteger(field.minLength) && text.length < field.minLength) return `Use at least ${field.minLength} characters.`;
  if (typeof text === "string" && Number.isInteger(field.maxLength) && text.length > field.maxLength) return `Use no more than ${field.maxLength} characters.`;
  if (typeof text === "string" && field.pattern && !new RegExp(`^(?:${field.pattern})$`).test(text)) return "Check the requested format.";
  return "";
}

export function isRegistrationTransportField(field) {
  return Boolean(field)
    && field.type !== "file"
    && field.presentation !== "terms-dialog";
}

export function createSignupForm({
  mount,
  schema,
  concept,
  registration,
  terms = TERMS_CONTENT,
  onCue,
  onPhaseChange,
  onSubmitRequest,
} = {}) {
  if (!(mount instanceof Element)) throw new TypeError("createSignupForm requires a mount Element.");
  if (!registration || typeof registration.mutationsEnabled !== "boolean") throw new TypeError("createSignupForm requires an explicit registration state.");
  assertEditableSchema(schema);
  let activeConcept = concept;
  let phaseIndex = 0;
  let complete = false;
  const root = document.createElement("div");
  root.className = "signup-component";
  root.innerHTML = buildMarkup(schema, activeConcept, registration, terms);
  mount.replaceChildren(root);

  const form = root.querySelector("[data-signup-form]");
  const status = root.querySelector("[data-form-status]");
  const summary = root.querySelector("[data-error-summary]");
  const back = root.querySelector('[data-action="back"]');
  const next = root.querySelector('[data-action="next"]');
  const count = root.querySelector("[data-current-count]");
  const profilePictureField = schema.find((field) => field.type === "file" && field.id === "profilePicture");
  const profilePictureInput = profilePictureField ? controlsFor(form, profilePictureField.id)[0] : null;
  const profilePictureImage = root.querySelector("[data-profile-picture-image]");
  const profilePicturePlaceholder = root.querySelector("[data-profile-picture-placeholder]");
  let profilePictureObjectUrl = "";

  const termsDialog = createTermsReviewDialog({
    mount: root.querySelector("[data-terms-dialog-mount]"),
    terms,
    registration,
    onConfirm: typeof onSubmitRequest === "function" ? handleTermsConfirmed : undefined,
  });

  function submissionReady() {
    return isRegistrationActivationReady(registration) && termsDialog.getState().bindingAvailable;
  }

  function revokeProfilePictureObjectUrl() {
    if (!profilePictureObjectUrl) return;
    URL.revokeObjectURL?.(profilePictureObjectUrl);
    profilePictureObjectUrl = "";
  }

  function syncProfilePicturePreview() {
    if (!profilePictureField || !profilePictureInput) return;
    const file = valueForField(profilePictureField, form);
    const preview = root.querySelector("[data-profile-picture-preview]");
    const statusNode = root.querySelector("[data-profile-picture-status]");
    const remove = root.querySelector('[data-action="remove-profile-picture"]');
    revokeProfilePictureObjectUrl();
    if (!file) {
      if (profilePictureImage) {
        profilePictureImage.removeAttribute("src");
        profilePictureImage.alt = "";
        profilePictureImage.hidden = true;
      }
      if (profilePicturePlaceholder) profilePicturePlaceholder.hidden = false;
      if (preview) preview.dataset.state = "empty";
      if (statusNode) statusNode.textContent = "No picture selected.";
      if (remove) remove.hidden = true;
      return;
    }
    if (remove) remove.hidden = false;
    const error = fieldError(profilePictureField, form);
    if (error) {
      if (profilePictureImage) {
        profilePictureImage.removeAttribute("src");
        profilePictureImage.alt = "";
        profilePictureImage.hidden = true;
      }
      if (profilePicturePlaceholder) profilePicturePlaceholder.hidden = false;
      if (preview) preview.dataset.state = "rejected";
      if (statusNode) statusNode.textContent = "Selection rejected. Choose a supported image within the size limit.";
      return;
    }
    profilePictureObjectUrl = URL.createObjectURL(file);
    if (profilePictureImage) {
      profilePictureImage.src = profilePictureObjectUrl;
      profilePictureImage.alt = "Local profile picture preview";
      profilePictureImage.hidden = false;
    }
    if (profilePicturePlaceholder) profilePicturePlaceholder.hidden = true;
    if (preview) preview.dataset.state = "selected";
    if (statusNode) statusNode.textContent = `${file.type.replace("image/", "").toUpperCase()} selected // ${(file.size / (1024 * 1024)).toFixed(2)} MiB // local only`;
  }

  function updateCharacterCount(fieldId) {
    const field = schema.find((item) => item.id === fieldId);
    const counter = root.querySelector(`[data-character-count="${fieldId}"]`);
    if (!field || !counter) return;
    counter.textContent = `${String(valueForField(field, form)).length} / ${field.maxLength}`;
  }

  function clearFieldError(field) {
    const shell = root.querySelector(`[data-field-shell="${field.id}"]`);
    const error = root.querySelector(`[data-error-for="${field.id}"]`);
    if (shell) delete shell.dataset.validity;
    if (error) error.textContent = "";
    controlsFor(form, field.id).forEach((control) => control.removeAttribute("aria-invalid"));
  }

  function syncConditionalFields({ clearInactive = false, announce = false } = {}) {
    schema.forEach((field) => {
      const active = isFieldActive(field, form);
      const shell = root.querySelector(`[data-field-shell="${field.id}"]`);
      if (!shell) return;
      if (field.presentation === "terms-dialog") {
        shell.hidden = !active;
        shell.dataset.conditionalState = active ? "active" : "inactive";
        if (!active) clearFieldError(field);
        return;
      }
      shell.hidden = !active;
      shell.dataset.conditionalState = active ? "active" : "inactive";
      controlsFor(form, field.id).forEach((control, index) => {
        control.disabled = !active;
        control.required = active && field.required && (field.type !== "checkbox-group" || index === 0);
        if (!active && clearInactive) clearControl(control);
      });
      if (!active) clearFieldError(field);
      updateCharacterCount(field.id);
    });
    if (announce) {
      const selection = namedValue(form, "secondLifeStatus");
      status.textContent = selection === "second-life"
        ? "Second Life identity questions are ready."
        : selection === "not-second-life"
          ? "Standard identity questions are ready."
          : `Phase ${phaseIndex + 1} of ${SIGNUP_PHASES.length}`;
    }
  }

  function render() {
    const phase = SIGNUP_PHASES[phaseIndex];
    const registrationReady = submissionReady();
    root.dataset.phase = complete ? "complete" : phase.id;
    root.querySelectorAll(".signup-phase").forEach((node) => {
      node.hidden = complete || node.dataset.phase !== phase.id;
    });
    root.querySelector(".signup-complete").hidden = !complete;
    root.querySelector(".signup-actions").hidden = complete;
    root.querySelectorAll("[data-progress]").forEach((node, index) => {
      node.dataset.state = complete || index < phaseIndex ? "complete" : index === phaseIndex ? "current" : "future";
      if (!complete && index === phaseIndex) node.setAttribute("aria-current", "step");
      else node.removeAttribute("aria-current");
    });
    back.hidden = phaseIndex === 0;
    const finalPhase = phaseIndex === SIGNUP_PHASES.length - 1;
    next.querySelector("span").textContent = finalPhase
      ? registrationReady ? "Review Terms & create" : "Registration locked"
      : "Continue";
    next.disabled = finalPhase && !registrationReady;
    next.setAttribute("aria-disabled", String(next.disabled));
    count.textContent = String(phaseIndex + 1).padStart(2, "0");
    status.textContent = complete ? "Enrollment request accepted by the connected account service." : finalPhase && !registrationReady ? registration.publicMessage : `Phase ${phaseIndex + 1} of ${SIGNUP_PHASES.length}: ${phase.label}`;
    syncConditionalFields();
    onPhaseChange?.(complete ? "complete" : phase.id, phaseIndex);
  }

  function clearErrors() {
    summary.hidden = true;
    summary.replaceChildren();
    schema.forEach(clearFieldError);
  }

  function validatePhase({ focus = true, includeTermsConsent = true } = {}) {
    clearErrors();
    const phase = SIGNUP_PHASES[phaseIndex];
    const errors = [];
    visibleFieldsForPhase(phase.id, schema)
      .filter((field) => isFieldActive(field, form) && (includeTermsConsent || field.presentation !== "terms-dialog"))
      .forEach((field) => {
      const controls = controlsFor(form, field.id);
      if (!controls.length) return;
      const message = fieldError(field, form);
      const shell = root.querySelector(`[data-field-shell="${field.id}"]`);
      shell.dataset.validity = message ? "invalid" : "valid";
      if (!message) return;
      controls.forEach((control) => control.setAttribute("aria-invalid", "true"));
      root.querySelector(`[data-error-for="${field.id}"]`).textContent = message;
      errors.push({ field, control: controls.find((control) => !control.disabled) || controls[0], message });
    });
    if (!errors.length) {
      onCue?.("valid", { phase: phase.id });
      return true;
    }
    const heading = document.createElement("strong");
    heading.textContent = "Check the highlighted fields";
    const list = document.createElement("ul");
    errors.forEach(({ field, message }) => {
      const item = document.createElement("li");
      item.textContent = `${field.label}: ${message}`;
      list.append(item);
    });
    summary.append(heading, list);
    summary.hidden = false;
    if (focus) {
      summary.focus();
      errors[0].control.focus({ preventScroll: false });
    }
    onCue?.("invalid", { phase: phase.id, count: errors.length });
    return false;
  }

  function privateSubmissionValues() {
    const values = {};
    schema.filter((field) => isFieldActive(field, form)).forEach((field) => {
      if (!isRegistrationTransportField(field)) return;
      const value = valueForField(field, form);
      values[field.id] = Array.isArray(value) ? Object.freeze([...value]) : value;
    });
    return Object.freeze(values);
  }

  async function handleTermsConfirmed({ consent } = {}) {
    const exactPolicy = Boolean(
      consent?.accepted === true
      && consent.policyId === terms.policyId
      && consent.version === terms.version
      && consent.contentRevision === terms.contentRevision
      && consent.effectiveDate === terms.effectiveDate
      && consent.locale === terms.locale
    );
    if (
      !submissionReady()
      || typeof onSubmitRequest !== "function"
      || phaseIndex !== SIGNUP_PHASES.length - 1
      || complete
      || !exactPolicy
    ) throw new Error("Registration or exact policy consent is not ready.");

    const result = await onSubmitRequest(Object.freeze({
      account: privateSubmissionValues(),
      consent,
    }));
    if (result === false) throw new Error("The account request was not accepted.");

    controlsFor(form, "password").forEach(clearControl);
    controlsFor(form, "confirmPassword").forEach(clearControl);
    schema.filter((field) => field.sensitive).forEach((field) => controlsFor(form, field.id).forEach(clearControl));
    complete = true;
    render();
    onCue?.("complete", {});
    root.querySelector(".signup-complete")?.focus();
    return result ?? true;
  }

  function reset() {
    termsDialog.close("reset");
    revokeProfilePictureObjectUrl();
    resetControls(form);
    syncProfilePicturePreview();
    phaseIndex = 0;
    complete = false;
    clearErrors();
    syncConditionalFields({ clearInactive: true });
    render();
    onCue?.("reset", {});
  }

  function onSubmit(event) {
    event.preventDefault();
    const finalPhase = phaseIndex === SIGNUP_PHASES.length - 1;
    if (finalPhase && !submissionReady()) {
      status.textContent = registration.publicMessage;
      return;
    }
    if (!validatePhase({ includeTermsConsent: !finalPhase })) return;
    if (!finalPhase) {
      phaseIndex += 1;
      render();
      onCue?.("advance", { phase: SIGNUP_PHASES[phaseIndex].id });
      root.querySelector('.signup-phase:not([hidden]) input:not(:disabled), .signup-phase:not([hidden]) select:not(:disabled), .signup-phase:not([hidden]) textarea:not(:disabled)')?.focus();
      return;
    }
    termsDialog.open({ intent: "binding", trigger: next });
    onCue?.("review", { phase: SIGNUP_PHASES[phaseIndex].id });
  }

  function onClick(event) {
    const action = event.target.closest("[data-action]")?.dataset.action;
    if (action === "back" && phaseIndex > 0) {
      phaseIndex -= 1;
      clearErrors();
      render();
      onCue?.("back", { phase: SIGNUP_PHASES[phaseIndex].id });
    }
    if (action === "review-terms") {
      clearFieldError(schema.find((field) => field.id === "acceptTerms"));
      termsDialog.open({
        intent: "preview",
        trigger: event.target.closest('[data-action="review-terms"]'),
      });
    }
    if (action === "reset") reset();
    if (action === "remove-profile-picture" && profilePictureInput) {
      revokeProfilePictureObjectUrl();
      profilePictureInput.value = "";
      clearFieldError(profilePictureField);
      syncProfilePicturePreview();
      profilePictureInput.focus();
      onCue?.("profile-picture-removed", {});
    }
    const reveal = event.target.closest("[data-reveal]");
    if (reveal) {
      const input = reveal.parentElement.querySelector("input");
      const visible = input.type === "text";
      input.type = visible ? "password" : "text";
      reveal.textContent = visible ? "Show" : "Hide";
      reveal.setAttribute("aria-pressed", String(!visible));
    }
  }

  function onChange(event) {
    if (schema.some((field) => field.showWhen?.field === event.target.name)) {
      syncConditionalFields({ clearInactive: true, announce: true });
    }
    const field = schema.find((item) => item.id === event.target.name);
    if (field?.type === "file") {
      syncProfilePicturePreview();
      const message = fieldError(field, form);
      const shell = root.querySelector(`[data-field-shell="${field.id}"]`);
      const error = root.querySelector(`[data-error-for="${field.id}"]`);
      if (shell) shell.dataset.validity = message ? "invalid" : hasFieldValue(field, form) ? "valid" : "idle";
      if (error) error.textContent = message;
      controlsFor(form, field.id).forEach((control) => {
        if (message) control.setAttribute("aria-invalid", "true");
        else control.removeAttribute("aria-invalid");
      });
      onCue?.(message ? "invalid" : "profile-picture-selected", { field: field.id });
    } else if (field) clearFieldError(field);
  }

  function onInput(event) {
    updateCharacterCount(event.target.name);
  }

  function onFocusIn(event) {
    const shell = event.target.closest("[data-field-shell]");
    if (shell) {
      shell.dataset.focused = "true";
      onCue?.("focus", { field: event.target.name });
    }
  }

  function onFocusOut(event) {
    const shell = event.target.closest("[data-field-shell]");
    if (!shell) return;
    delete shell.dataset.focused;
    const field = schema.find((item) => item.id === event.target.name);
    if (!field || !isFieldActive(field, form)) return;
    const message = fieldError(field, form);
    shell.dataset.validity = message ? "invalid" : hasFieldValue(field, form) ? "valid" : "idle";
  }

  function serializableValues() {
    const values = {};
    schema.filter((field) => isFieldActive(field, form)).forEach((field) => {
      if (field.type === "password" || field.type === "file" || field.presentation === "terms-dialog" || field.sensitive) return;
      values[field.id] = valueForField(field, form);
    });
    return values;
  }

  form.addEventListener("submit", onSubmit);
  form.addEventListener("change", onChange);
  form.addEventListener("input", onInput);
  root.addEventListener("click", onClick);
  root.addEventListener("focusin", onFocusIn);
  root.addEventListener("focusout", onFocusOut);
  syncProfilePicturePreview();
  syncConditionalFields({ clearInactive: true });
  render();

  return {
    setConcept(nextConcept) {
      activeConcept = nextConcept;
      root.querySelector(".signup-console").dataset.conceptForm = nextConcept.id;
    },
    getState() {
      return {
        registration: { enabled: registration.enabled, mutationsEnabled: registration.mutationsEnabled, ready: isRegistrationActivationReady(registration), status: registration.status },
        phase: complete ? "complete" : SIGNUP_PHASES[phaseIndex].id,
        phaseIndex,
        complete,
        conceptId: activeConcept.id,
        values: serializableValues(),
        profilePicture: Object.freeze({
          selected: Boolean(profilePictureField && valueForField(profilePictureField, form)),
          valid: Boolean(profilePictureField && valueForField(profilePictureField, form) && !fieldError(profilePictureField, form)),
          type: profilePictureField ? valueForField(profilePictureField, form)?.type || null : null,
          size: profilePictureField ? valueForField(profilePictureField, form)?.size || 0 : 0,
          localOnly: true,
          includedInSubmission: false,
        }),
        termsReview: termsDialog.getState(),
      };
    },
    destroy() {
      revokeProfilePictureObjectUrl();
      termsDialog.destroy();
      form.removeEventListener("submit", onSubmit);
      form.removeEventListener("change", onChange);
      form.removeEventListener("input", onInput);
      root.removeEventListener("click", onClick);
      root.removeEventListener("focusin", onFocusIn);
      root.removeEventListener("focusout", onFocusOut);
      root.remove();
    },
  };
}


